import { useEffect, useMemo, useRef, useState } from "react"
import { useSearchParams } from "react-router-dom"
import moment from "moment"
import nProgress from "nprogress"
import axios from "axios"
import toast from "react-hot-toast"

import { useAuth } from "contexts/auth-context"
import { Modal, Pagination } from "components"
import Table from "components/table"
import Label from "components/forms/label"
import OutletFilterSelect from "components/forms/outlet-filter-select"
import { getDefaultOutletFilterValue, resolveOutletFilter } from "helpers/general-helper"

import useBranchRepositories from "repositories/branch"
import useCustomerNoteRepositories from "repositories/customer-note"
import Filter from "./filter"
import FormModal from "./form-modal"
import SettlementModal from "./settlement-modal"
import CustomerNotePrintDocument from "./print-document"
import SettlementFakturDocument from "./settlement-faktur-document"
import { useReactToPrint } from "react-to-print"

export default function List() {
  // 150726 (ATALLA-395): user dipakai utk "Dicetak Oleh" pada dokumen print.
  const { can, isSuper, currentBranch, canFilterAllOutlets, user } = useAuth()

  const [searchParams, setSearchParams] = useSearchParams()

  const searchInput = useRef(null)

  const [openModal, setOpenModal] = useState(false)
  const [editingNote, setEditingNote] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [settlementTarget, setSettlementTarget] = useState(null)
  const [isSubmittingSettlement, setIsSubmittingSettlement] = useState(false)
  const [customerMetaById, setCustomerMetaById] = useState({})
  const [detailNote, setDetailNote] = useState(null)
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  const page = searchParams.get("page")
  const search = searchParams.get("search")

  const isBranchLocked = !canFilterAllOutlets() && !!currentBranch?.id // ATALLA-403: pemegang permission filter-entity bebas pilih outlet entitasnya
  const rawOutletId = searchParams.get("outlet_id") || getDefaultOutletFilterValue()
  const outletId = isBranchLocked ? String(currentBranch.id) : rawOutletId
  // Grup → CSV id (mis. "1,15") utk filter list; outlet_id tunggal utk chip filter & default form.
  const { outlet_id: resolvedOutletId, outlet_id_csv: resolvedOutletCsv } = resolveOutletFilter(outletId)
  const outlet = resolvedOutletId || null
  const type = searchParams.get("type")
  const status = searchParams.get("status")

  const dateFrom = searchParams.get("date_from")
  const dateTo = searchParams.get("date_to")

  const orderBy = searchParams.get("order_by")
  const direction = searchParams.get("direction")
  const effectiveOrderBy = orderBy || "created_at"
  const effectiveDirection = direction || "desc"

  const [searchText, setSearchText] = useState(search ?? "")

  const { data: branchSelection } = useBranchRepositories({ paginate: false })

  // ATALLA-384: print credit/debit note via react-to-print (pola sama dgn print PO).
  const [printNote, setPrintNote] = useState(null)
  const printRef = useRef(null)
  const handlePrint = useReactToPrint({
    documentTitle: `${
      String(printNote?.type || "").toUpperCase() === "DEBIT" ? "Debit Note" : "Credit Note"
    } ${printNote?.note_number || ""}`.trim(),
    removeAfterPrint: true,
    onAfterPrint: () => setPrintNote(null),
  })
  useEffect(() => {
    if (printNote && printRef.current) {
      handlePrint(null, () => printRef.current)
    }
  }, [printNote]) // eslint-disable-line react-hooks/exhaustive-deps
  const printOutletName = useMemo(() => {
    if (!printNote) return ""
    const branches = Array.isArray(branchSelection) ? branchSelection : []
    return branches.find((b) => String(b.id) === String(printNote.outlet_id))?.name || ""
  }, [printNote, branchSelection])

  // 230726 (permintaan user): FAKTUR PELUNASAN debit note — dicetak otomatis
  // setelah pelunasan tersimpan & bisa dicetak ulang dari tombol Faktur di baris.
  // Data = note + seluruh usages TYPE_SETTLEMENT (dari GET /customer-notes/{id}).
  const [fakturData, setFakturData] = useState(null)
  const fakturRef = useRef(null)
  const handlePrintFaktur = useReactToPrint({
    documentTitle: `Faktur Pelunasan ${fakturData?.note?.note_number || ""}`.trim(),
    removeAfterPrint: true,
    onAfterPrint: () => setFakturData(null),
  })
  useEffect(() => {
    if (fakturData && fakturRef.current) {
      handlePrintFaktur(null, () => fakturRef.current)
    }
  }, [fakturData]) // eslint-disable-line react-hooks/exhaustive-deps
  const fakturOutletName = useMemo(() => {
    if (!fakturData?.note) return ""
    const branches = Array.isArray(branchSelection) ? branchSelection : []
    return branches.find((b) => String(b.id) === String(fakturData.note.outlet_id))?.name || ""
  }, [fakturData, branchSelection])

  const openSettlementFaktur = async (row) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes/${row.id}`
      )
      const detail = response?.data?.data
      if (!detail) throw new Error("Detail note tidak ditemukan")

      const settlements = (Array.isArray(detail.usages) ? detail.usages : [])
        .filter((u) =>
          String(u?.usage_type || "").toUpperCase() === "SETTLEMENT" &&
          String(u?.record_status || "N").toUpperCase() !== "D"
        )
        .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))

      setFakturData({ note: detail, settlements })
    } catch (error) {
      toast.error(error?.response?.data?.message || "Gagal memuat faktur pelunasan")
    }
  }

  const {
    data: customerNoteRaw,
    isLoading,
    mutate,
  } = useCustomerNoteRepositories({
    page,
    search,
    outlet_id: resolvedOutletCsv || undefined,
    type,
    status,
    date_from: dateFrom,
    date_to: dateTo,
    order_by: effectiveOrderBy,
    direction: effectiveDirection,
  })

  const customerNoteData = useMemo(() => customerNoteRaw ?? {}, [customerNoteRaw])

  useEffect(() => {
    if (isLoading) nProgress.start()
    else nProgress.done()
  }, [isLoading])

  useEffect(() => {
    searchInput.current?.focus()
  }, [])

  useEffect(() => {
    setSearchText(search ?? "")
  }, [search])

  useEffect(() => {
    const rows = customerNoteData?.data ?? []
    const missingCustomerIds = rows
      .map((item) => String(item?.customer_id || ""))
      .filter((id) => id && !customerMetaById[id])

    if (!missingCustomerIds.length) return

    let isCancelled = false

    const loadCustomers = async () => {
      const entries = await Promise.all(
        missingCustomerIds.map(async (customerId) => {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_YOIBOI_API_URL}/api/v1/customers`,
              {
                params: {
                  search: customerId,
                  pagination: true,
                },
              }
            )

            const rows = response?.data?.data?.data ?? []
            const matched =
              rows.find((item) => String(item?.id) === customerId) || rows[0]

            if (!matched) {
              return [customerId, { name: "", phone: "" }]
            }

            return [
              customerId,
              {
                name:
                  matched.full_name ||
                  matched.name ||
                  matched.customer_name ||
                  "",
                phone:
                  matched.phone_number ||
                  matched.phone ||
                  matched.mobile_phone ||
                  "",
              },
            ]
          } catch (_error) {
            return [customerId, { name: "", phone: "" }]
          }
        })
      )

      if (isCancelled) return

      setCustomerMetaById((prev) => ({
        ...prev,
        ...Object.fromEntries(entries),
      }))
    }

    loadCustomers()

    return () => {
      isCancelled = true
    }
  }, [customerNoteData, customerMetaById])

  const mergeParams = (next) => {
    const current = Object.fromEntries(searchParams.entries())
    const merged = { ...current, ...next }

    Object.keys(merged).forEach((key) => {
      if (
        merged[key] === null ||
        merged[key] === undefined ||
        merged[key] === ""
      ) {
        delete merged[key]
      }
    })

    setSearchParams(merged)
  }

  const commitSearch = (value) => {
    mergeParams({
      search: value || null,
      page: 1,
    })
  }

  const updateSearch = (value) => {
    setSearchText(value)
  }

  const clearSearch = () => {
    setSearchText("")
    commitSearch("")
    searchInput.current?.focus()
  }

  const toggleSort = (value) => {
    if ((direction === null || direction === "desc") || orderBy !== value) {
      mergeParams({
        order_by: value,
        direction: "asc",
        page: 1,
      })
    } else {
      mergeParams({
        order_by: value,
        direction: "desc",
        page: 1,
      })
    }
  }

  const formatCurrency = (value) => {
    return Number(value ?? 0).toLocaleString("id-ID")
  }

  const formatHistoryDescription = (noteType, usage) => {
    const description = String(usage?.description || "-")
    return description
  }

  const formatHistoryAmount = (noteType, usage) => {
    const typeValue = String(noteType || "").toUpperCase()
    const usageType = String(usage?.usage_type || "").toUpperCase()
    const amount = formatCurrency(Math.abs(Number(usage?.used_amount ?? 0)))

    if (typeValue === "CREDIT") {
      if (usageType === "PAYMENT") {
        return `Rp -${amount}`
      }

      if (usageType === "REVERSAL") {
        return `Rp +${amount}`
      }
    }

    if (typeValue === "DEBIT") {
      if (usageType === "SETTLEMENT") {
        return `Rp -${amount}`
      }

      if (usageType === "REVERSAL") {
        return `Rp +${amount}`
      }
    }

    return `Rp ${amount}`
  }

  const getCustomerName = (v) => {
    const meta = customerMetaById[String(v.customer_id || "")]
    return (
      v.customer_name ||
      v.customer?.full_name ||
      v.customer?.name ||
      meta?.name ||
      v.customer_id ||
      "-"
    )
  }

  const getCustomerPhone = (v) => {
    const meta = customerMetaById[String(v.customer_id || "")]
    return (
      v.customer_phone ||
      v.customer?.phone_number ||
      v.customer?.phone ||
      v.customer?.mobile_phone ||
      meta?.phone ||
      "-"
    )
  }

  const showDetail = async (row) => {
    try {
      setIsLoadingDetail(true)
      const response = await axios.get(
        `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes/${row?.id}`
      )

      const detail = response?.data?.data || row
      const usages = Array.isArray(detail?.usages) ? detail.usages : []
      const noteType = String(detail?.type || "").toUpperCase()
      const relevantHistory = usages
        .filter((usage) => {
          const usageType = String(usage?.usage_type || "").toUpperCase()

          if (noteType === "DEBIT") {
            return ["SETTLEMENT", "REVERSAL"].includes(usageType)
          }

          if (noteType === "CREDIT") {
            return ["PAYMENT", "REVERSAL"].includes(usageType)
          }

          return false
        })
        .sort((a, b) => new Date(b?.created_at || 0) - new Date(a?.created_at || 0))

      setDetailNote({
        ...detail,
        historyTitle:
          noteType === "DEBIT"
            ? "Histori Debit Note"
            : noteType === "CREDIT"
            ? "Histori Credit Note"
            : "Histori Note",
        relevantHistory,
      })
    } catch (_error) {
      setDetailNote({
        ...row,
        historyTitle: "Histori Note",
        relevantHistory: [],
      })
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const canManageCustomerNote =
    can("customer-notes.create") ||
    can("customer-notes.edit") ||
    can("customer-notes.update")

  const canSettle = (row) => {
    const typeValue = String(row?.type || "").toUpperCase()
    const statusValue = String(row?.status || "").toUpperCase()
    const remaining = Number(row?.remaining_amount ?? 0)

    return (
      canManageCustomerNote &&
      typeValue === "DEBIT" &&
      remaining > 0 &&
      statusValue !== "CANCELLED" &&
      statusValue !== "USED"
    )
  }

  const canEditNote = (row) => {
    const statusValue = String(row?.status || "").toUpperCase()
    const usedAmount = Number(row?.used_amount ?? 0)

    return (
      canManageCustomerNote &&
      usedAmount <= 0 &&
      statusValue !== "EXPIRED" &&
      statusValue !== "USED" &&
      statusValue !== "CANCELLED"
    )
  }

  const canCancelNote = (row) => {
    const statusValue = String(row?.status || "").toUpperCase()
    const usedAmount = Number(row?.used_amount ?? 0)

    return (
      canManageCustomerNote &&
      usedAmount <= 0 &&
      statusValue !== "EXPIRED" &&
      statusValue !== "USED" &&
      statusValue !== "CANCELLED"
    )
  }

  const dataListHeaders = [
    { isAction: false, content: "Nomor Note", sortKey: "note_number" },
    { isAction: false, content: "Customer", sortKey: "customer_id" },
    { isAction: false, content: "Outlet", sortKey: "outlet_id" },
    { isAction: false, content: "Tipe", sortKey: "type" },
    { isAction: false, content: "Awal", sortKey: "original_amount" },
    { isAction: false, content: "Terpakai", sortKey: "used_amount" },
    { isAction: false, content: "Sisa", sortKey: "remaining_amount" },
    { isAction: false, content: "Status", sortKey: "status" },
    { isAction: false, content: "Tanggal Terbit", sortKey: "issue_date" },
    { isAction: false, content: "Dibuat Oleh", sortKey: "created_by" },
    { isAction: true, content: "Aksi" },
  ]

  const dataListColumns = [
    {
      type: "default",
      bold: true,
      content: (v) => (
        <div className="flex flex-col">
          <span className="font-semibold text-neutral-800">
            {v.note_number || "-"}
          </span>

          {!!v.source_type && (
            <span className="text-xs text-neutral-500">
              {v.source_type}
              {v.source_id ? ` • ${v.source_id}` : ""}
            </span>
          )}
        </div>
      ),
    },

    {
      type: "default",
      content: (v) => (
        <div className="flex flex-col">
          <span className="font-medium text-neutral-800">
            {getCustomerName(v)}
          </span>
          <span className="text-xs text-neutral-500">
            {getCustomerPhone(v)}
          </span>
        </div>
      ),
    },

    {
      type: "default",
      content: (v) => v.outlet?.name || v.outlet_name || v.outlet_id || "-",
    },

    {
      type: "default",
      content: (v) => {
        const isCredit = v.type === "CREDIT"

        return (
          <span
            className={`px-2 py-1 text-xs rounded font-medium ${
              isCredit
                ? "bg-green-100 text-green-700"
                : "bg-orange-100 text-orange-700"
            }`}
          >
            {v.type || "-"}
          </span>
        )
      },
    },

    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-neutral-700">
          {formatCurrency(v.original_amount)}
        </span>
      ),
    },

    {
      type: "default",
      content: (v) => (
        <span className="font-semibold text-neutral-700">
          {formatCurrency(v.used_amount)}
        </span>
      ),
    },

    {
      type: "default",
      content: (v) => {
        const remaining = Number(v.remaining_amount ?? 0)

        return (
          <span
            className={`font-semibold ${
              remaining > 0 ? "text-blue-700" : "text-neutral-500"
            }`}
          >
            {formatCurrency(remaining)}
          </span>
        )
      },
    },

    {
      type: "default",
      content: (v) => {
        const statusMap = {
          OPEN: "bg-blue-100 text-blue-700",
          PARTIAL: "bg-yellow-100 text-yellow-700",
          USED: "bg-neutral-200 text-neutral-700",
          EXPIRED: "bg-red-100 text-red-700",
          CANCELLED: "bg-red-100 text-red-700",
        }

        return (
          <span
            className={`px-2 py-1 text-xs rounded font-medium ${
              statusMap[v.status] || "bg-neutral-100 text-neutral-700"
            }`}
          >
            {v.status || "-"}
          </span>
        )
      },
    },

    {
      type: "default",
      content: (v) => {
        const d = v.issue_date
        return d ? moment(d).format("YYYY-MM-DD HH:mm") : "-"
      },
    },

    {
      type: "default",
      content: (v) => v.created_by || "-",
    },

    {
      type: "action",
      content: (v) => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => showDetail(v)}
            className="inline-flex items-center px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition"
          >
            Detail
          </button>

          {canEditNote(v) ? (
            <button
              type="button"
              onClick={() => {
                setEditingNote(v)
                setOpenModal(true)
              }}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-amber-700 bg-amber-50 rounded-lg hover:bg-amber-100 transition"
            >
              Edit
            </button>
          ) : null}

          {canCancelNote(v) ? (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Batalkan customer note ${v?.note_number || ""}?`)) {
                  return
                }

                try {
                  await axios.delete(
                    `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes/${v.id}`
                  )
                  toast.success("Customer note berhasil dibatalkan")
                  await mutate()
                } catch (error) {
                  toast.error(error?.response?.data?.message || "Gagal membatalkan customer note")
                }
              }}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition"
            >
              Batalkan
            </button>
          ) : null}

          {canSettle(v) ? (
            <button
              type="button"
              onClick={() => setSettlementTarget(v)}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition"
            >
              Pelunasan
            </button>
          ) : null}

          {["CREDIT", "DEBIT"].includes(String(v?.type || "").toUpperCase()) ? (
            <button
              type="button"
              onClick={() => setPrintNote(v)}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-neutral-700 bg-neutral-100 rounded-lg hover:bg-neutral-200 transition"
            >
              Print
            </button>
          ) : null}

          {/* 230726 (permintaan user): faktur pelunasan — debit note yang sudah
              ada pembayarannya (termasuk pelunasan via credit note). */}
          {String(v?.type || "").toUpperCase() === "DEBIT" && Number(v?.used_amount || 0) > 0 ? (
            <button
              type="button"
              onClick={() => openSettlementFaktur(v)}
              className="inline-flex items-center px-3 py-2 text-xs font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition"
            >
              Faktur
            </button>
          ) : null}
        </div>
      ),
    },
  ]

  const updateFilter = (value) => {
    mergeParams({
      ...value,
      page: 1,
    })
  }

  const removeFilter = () => {
    setSearchParams({})
    setSearchText("")
  }

  const handleSubmit = async (payload) => {
    try {
      setIsSubmitting(true)

      // 230726 (permintaan user): kirim nama user login sbg created_by — axios
      // tanpa Bearer membuat $request->user() null di backend, sehingga
      // "Dibuat Oleh" di cetakan dulu muncul "SYSTEM" (pola ATALLA-343).
      const actorPayload = { ...payload, created_by: user?.name || undefined }

      if (editingNote?.id) {
        await axios.put(
          `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes/${editingNote.id}`,
          actorPayload
        )
      } else {
        await axios.post(
          `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes`,
          actorPayload
        )
      }

      setOpenModal(false)
      setEditingNote(null)
      toast.success(editingNote?.id ? "Customer note berhasil diperbarui" : "Customer note berhasil disimpan")
      await mutate()
    } catch (error) {
      toast.error(error?.response?.data?.message || "Gagal menyimpan customer note")
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSettlementSubmit = async (payload) => {
    const remaining = Number(settlementTarget?.remaining_amount ?? 0)

    if (!settlementTarget?.id) {
      toast.error("Debit note tidak valid")
      return
    }

    if (!payload.amount || payload.amount <= 0) {
      toast.error("Nominal pelunasan harus lebih dari 0")
      return
    }

    if (payload.amount > remaining) {
      toast.error("Nominal pelunasan melebihi sisa debit note")
      return
    }

    try {
      setIsSubmittingSettlement(true)

      const settledNote = settlementTarget

      await axios.post(
        `${process.env.REACT_APP_INVENTORY_API_URL}/customer-notes/${settlementTarget.id}/settle`,
        // 230726: created_by dari user login (lihat handleSubmit).
        { ...payload, created_by: user?.name || undefined }
      )

      setSettlementTarget(null)
      toast.success("Pelunasan debit note berhasil disimpan")
      await mutate()

      // 230726 (permintaan user): langsung cetak FAKTUR PELUNASAN setelah tersimpan
      // (termasuk pelunasan memakai credit note customer).
      openSettlementFaktur(settledNote)
    } catch (error) {
      toast.error(error?.response?.data?.message || "Gagal menyimpan pelunasan debit note")
    } finally {
      setIsSubmittingSettlement(false)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="relative">
          <input
            ref={searchInput}
            onChange={(e) => updateSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                commitSearch(searchText)
              }
            }}
            value={searchText}
            type="text"
            placeholder="Cari note / customer / sumber..."
            className="w-72 py-3 pl-4 pr-10 text-sm border rounded-xl border-neutral-200 focus:outline-none focus:ring"
          />

          {!!searchText && (
            <button
              type="button"
              onClick={clearSearch}
              className="absolute inset-y-0 right-3 flex items-center text-neutral-400 hover:text-neutral-700"
            >
              ✕
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="w-56">
            <Label value="Outlet" />
            <OutletFilterSelect
              value={outletId}
              onChange={(v) => mergeParams({ outlet_id: v, page: 1 })}
              disabled={isBranchLocked}
              className="w-full mt-1 p-2 text-sm transition border border-neutral-200 rounded-xl focus:outline-none focus:border-neutral-400 focus:ring focus:ring-neutral-200 disabled:bg-neutral-50 disabled:opacity-75"
            />
          </div>

          <Filter
            onSubmit={updateFilter}
            onRemove={removeFilter}
            data={Object.fromEntries(
              Object.entries({
                outlet,
                type,
                status,
                dateFrom,
                dateTo,
              }).filter(([_, v]) => v != null)
            )}
          />

          {can("customer-notes.create") && (
            <button
              onClick={() => {
                setEditingNote(null)
                setOpenModal(true)
              }}
              className="inline-flex items-center px-4 py-3 space-x-2 text-white transition bg-blue-600 rounded-xl active:hover:scale-95"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-4 h-4"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                fill="none"
              >
                <path d="M12 5v14" />
                <path d="M5 12h14" />
              </svg>
              <span>Tambah</span>
            </button>
          )}
        </div>
      </div>

      <Table
        isLoading={isLoading}
        data={customerNoteData?.data ?? []}
        headers={dataListHeaders}
        columns={dataListColumns}
        key={(v) => v.id}
        isEmpty={!customerNoteData?.data?.length && !search}
        isEmptyOnSearching={!customerNoteData?.data?.length && !!search}
        toggleSort={toggleSort}
        orderBy={effectiveOrderBy}
        direction={effectiveDirection}
      />

      <Pagination
        links={customerNoteData?.links}
        from={customerNoteData?.from}
        to={customerNoteData?.to}
        total={customerNoteData?.total}
      />

      <FormModal
        open={openModal}
        onClose={() => {
          if (!isSubmitting) {
            setOpenModal(false)
            setEditingNote(null)
          }
        }}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        defaultOutletId={outlet || ""}
        initialData={editingNote}
      />

      <SettlementModal
        open={!!settlementTarget}
        note={settlementTarget}
        onClose={() => {
          if (!isSubmittingSettlement) setSettlementTarget(null)
        }}
        onSubmit={handleSettlementSubmit}
        isSubmitting={isSubmittingSettlement}
      />

      <Modal
        isOpen={!!detailNote || isLoadingDetail}
        title="Detail Customer Note"
        size="3xl"
        onClose={() => {
          if (isLoadingDetail) return
          setDetailNote(null)
        }}
        content={
        <div className="w-full rounded-2xl overflow-hidden">
          <div className="flex items-start justify-between px-6 py-5 border-b border-neutral-200">
            <div>
              <div className="mt-1 text-sm text-neutral-500">
                {detailNote?.note_number || "Memuat detail note..."}
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                if (isLoadingDetail) return
                setDetailNote(null)
              }}
              className="inline-flex items-center justify-center w-9 h-9 text-neutral-500 rounded-lg hover:bg-neutral-100"
            >
              ✕
            </button>
          </div>

          <div className="px-6 py-5 space-y-5">
            {isLoadingDetail ? (
              <div className="py-12 text-sm text-center text-neutral-500">
                Memuat detail customer note...
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="p-4 border border-neutral-200 rounded-xl bg-neutral-50">
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Customer</div>
                    <div className="mt-1 text-sm font-medium text-neutral-900">
                      {getCustomerName(detailNote || {})}
                    </div>
                    <div className="mt-1 text-sm text-neutral-500">
                      {getCustomerPhone(detailNote || {})}
                    </div>
                  </div>

                  <div className="p-4 border border-neutral-200 rounded-xl bg-neutral-50">
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Tipe & Status</div>
                    <div className="mt-1 flex items-center gap-2">
                      <span className="px-2 py-1 text-xs font-medium text-neutral-800 rounded bg-white border border-neutral-200">
                        {detailNote?.type || "-"}
                      </span>
                      <span className="px-2 py-1 text-xs font-medium text-neutral-800 rounded bg-white border border-neutral-200">
                        {detailNote?.status || "-"}
                      </span>
                    </div>
                    <div className="mt-2 text-sm text-neutral-500">
                      Outlet: {detailNote?.outlet?.name || detailNote?.outlet_name || detailNote?.outlet_id || "-"}
                    </div>
                    {/* 150726 (ATALLA-393): keterangan asal dana (Cash/Transfer) di detail credit note. */}
                    {String(detailNote?.type || "").toUpperCase() === "CREDIT" ? (
                      <div className="mt-2 text-sm text-neutral-500">
                        Metode:{" "}
                        <span className="px-2 py-0.5 text-xs font-medium text-emerald-800 rounded bg-emerald-50 border border-emerald-200">
                          {String(detailNote?.payment_method_type || "").toUpperCase() === "TRANSFER"
                            ? "Transfer"
                            : String(detailNote?.payment_method_type || "").toUpperCase() === "CASH"
                            ? "Cash"
                            : "-"}
                        </span>
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <div className="p-4 border border-neutral-200 rounded-xl">
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Awal</div>
                    <div className="mt-2 text-lg font-semibold text-neutral-900">
                      Rp {formatCurrency(detailNote?.original_amount)}
                    </div>
                  </div>

                  <div className="p-4 border border-neutral-200 rounded-xl">
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Terpakai</div>
                    <div className="mt-2 text-lg font-semibold text-neutral-900">
                      Rp {formatCurrency(detailNote?.used_amount)}
                    </div>
                  </div>

                  <div className="p-4 border border-neutral-200 rounded-xl">
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Sisa</div>
                    <div className="mt-2 text-lg font-semibold text-blue-700">
                      Rp {formatCurrency(detailNote?.remaining_amount)}
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Tanggal Terbit</div>
                    <div className="mt-1 text-sm text-neutral-900">
                      {detailNote?.issue_date ? moment(detailNote.issue_date).format("YYYY-MM-DD HH:mm") : "-"}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs font-semibold tracking-wide text-neutral-500">Tanggal Kadaluarsa</div>
                    <div className="mt-1 text-sm text-neutral-900">
                      {detailNote?.expiry_date ? moment(detailNote.expiry_date).format("YYYY-MM-DD HH:mm") : "-"}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="text-xs font-semibold tracking-wide text-neutral-500">Keterangan</div>
                  <div className="mt-1 text-sm text-neutral-800 whitespace-pre-wrap">
                    {detailNote?.description || "-"}
                  </div>
                </div>

                <div className="border border-neutral-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-3 bg-neutral-50 border-b border-neutral-200">
                    <div className="text-sm font-semibold text-neutral-900">
                      {detailNote?.historyTitle || "Histori Note"}
                    </div>
                  </div>

                  <div className="max-h-80 overflow-y-auto divide-y divide-neutral-100">
                    {Array.isArray(detailNote?.relevantHistory) && detailNote.relevantHistory.length > 0 ? (
                      detailNote.relevantHistory.map((usage) => (
                        <div key={usage?.id} className="px-4 py-3">
                          <div className="flex items-start justify-between gap-4">
                            <div>
                              <div className="text-sm font-medium text-neutral-900">
                                {formatHistoryDescription(detailNote?.type, usage)}
                              </div>
                              <div className="mt-1 text-xs text-neutral-500">
                                {usage?.created_at ? moment(usage.created_at).format("YYYY-MM-DD HH:mm") : "-"}
                                {usage?.created_by ? ` • ${usage.created_by}` : ""}
                              </div>
                            </div>
                            <div className="text-sm font-semibold text-neutral-900 whitespace-nowrap">
                              {formatHistoryAmount(detailNote?.type, usage)}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <div className="px-4 py-8 text-sm text-center text-neutral-500">
                        {String(detailNote?.type || "").toUpperCase() === "DEBIT"
                          ? "Belum ada histori pembayaran debit note."
                          : String(detailNote?.type || "").toUpperCase() === "CREDIT"
                          ? "Belum ada histori pemakaian credit note."
                          : "Belum ada histori note."}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        }
      />

      {/* ATALLA-384: dokumen cetak credit note (hidden, hanya tampil saat print). */}
      <CustomerNotePrintDocument ref={printRef} note={printNote} outletName={printOutletName} printedBy={user?.name} />
      <SettlementFakturDocument
        ref={fakturRef}
        note={fakturData?.note}
        settlements={fakturData?.settlements || []}
        outletName={fakturOutletName}
        printedBy={user?.name}
      />
    </div>
  )
}
